import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { useSOSStore } from '@/stores/sos'
import { usePanicStore } from '@/stores/panic'
import { useUIStore } from '@/stores/ui'
import { useSearchModalStore } from '@/stores/searchModal'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { ROUTES } from '@/constants/routes'
import { USER_ROLES } from '@/constants/roles'
import { useMessagingUnread } from '@/lib/useMessagingUnread'
import { useBellUnread } from '@/lib/useBellUnread'

const ROUTE_TITLES: { pattern: RegExp; title: string; subtitle: string }[] = [
  { pattern: /^\/home$/, title: 'Home', subtitle: 'Your safety dashboard' },
  { pattern: /^\/profiles\/[^/]+$/, title: 'Profile Detail', subtitle: 'Medical info · Emergency contacts · Privacy' },
  { pattern: /^\/profiles$/, title: 'Safety Profiles', subtitle: 'Medical info · Emergency contacts · QR card' },
  { pattern: /^\/sos$/, title: 'SOS', subtitle: 'One tap alerts your emergency contacts' },
  { pattern: /^\/location$/, title: 'Location', subtitle: 'Real-time location sharing & safe zones' },
  { pattern: /^\/sessions$/, title: 'Sessions', subtitle: "Clips and notes from your child's sessions" },
  { pattern: /^\/community$/, title: 'Community', subtitle: 'Resources for special needs families' },
  { pattern: /^\/find-help$/, title: 'Find Help', subtitle: 'Aid programs · Service providers · Jobs' },
  { pattern: /^\/messaging\/[^/]+$/, title: 'Message Thread', subtitle: '' },
  { pattern: /^\/messaging$/, title: 'Messages', subtitle: 'Private messages with your support network' },
  { pattern: /^\/settings$/, title: 'Settings', subtitle: 'Account & app preferences' },
  { pattern: /^\/feedback$/, title: 'Feedback', subtitle: "Vote on features · Report bugs · See what's next" },
  { pattern: /^\/mission-control$/, title: 'Mission Control', subtitle: 'Active alerts and incident status' },
  { pattern: /^\/org\/team$/, title: 'Org Team', subtitle: 'Staff members & access management' },
  { pattern: /^\/org$/, title: 'Organization', subtitle: 'Manage your organization' },
  { pattern: /^\/command-center$/, title: 'Command Center', subtitle: 'Platform administration' },
  { pattern: /^\/hazards$/, title: 'Hazard Watch', subtitle: 'Active disaster alerts near you' },
  { pattern: /^\/weather$/, title: 'Weather', subtitle: 'Current conditions + 7-day forecast' },
  { pattern: /^\/invite\/[^/]+$/, title: 'Invitation', subtitle: 'Co-carer invite' },
  { pattern: /^\/org-invite\/[^/]+$/, title: 'Org Invitation', subtitle: 'Organization invite' },
  { pattern: /^\/org\/apply$/, title: 'Apply as Org', subtitle: 'Register your organization' },
  { pattern: /^\/org\/enrolled$/, title: 'Enrolled', subtitle: 'Clients & families connected to your org' },
  { pattern: /^\/org\/notes$/, title: 'Session Notes', subtitle: 'Track observations and progress' },
  { pattern: /^\/org\/sos$/, title: 'SOS Feed', subtitle: 'Active alerts from enrolled individuals' },
  { pattern: /^\/org\/location$/, title: 'Location', subtitle: 'Live locations of enrolled profiles' },
  { pattern: /^\/org\/camera$/, title: 'Session Clips', subtitle: 'Record and share session videos' },
  { pattern: /^\/org\/pending$/, title: 'Application Pending', subtitle: 'Your org application is under review' },
  { pattern: /^\/org\/settings$/, title: 'Org Settings', subtitle: 'Manage your organization details' },
  { pattern: /^\/qr$/, title: 'QR Code', subtitle: 'Emergency card for your profile' },
  { pattern: /^\/aac\/[^/]+$/, title: 'My Voice', subtitle: 'Tap-to-talk communication board' },
  { pattern: /^\/about$/, title: "About David's Beacon", subtitle: 'Mission, story & contact' },
  { pattern: /^\/privacy$/, title: 'Privacy Policy', subtitle: "How David's Beacon handles your data" },
  { pattern: /^\/privacy-policy$/, title: 'Privacy Policy', subtitle: "How David's Beacon handles your data" },
  { pattern: /^\/security-privacy$/, title: 'Security & Privacy', subtitle: 'How we protect families and organizations' },
  { pattern: /^\/terms$/, title: 'Terms of Use', subtitle: 'Platform rules & responsibilities' },
]

function getRouteInfo(pathname: string) {
  return ROUTE_TITLES.find(r => r.pattern.test(pathname))
}

export function AppHeader({ scrollHidden = false }: { scrollHidden?: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const primaryRole = useAuthStore((s) => s.primaryRole())
  const user = useAuthStore((s) => s.user)
  const roles = useAuthStore((s) => s.roles())
  const managedProfiles = useAuthStore((s) => s.userDoc?.managedProfiles)
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin())
  const isSOSActive = useSOSStore((s) => s.isSOSActive)
  const isPanicActive = usePanicStore((s) => s.isPanicActive)
  const openSidebar = useUIStore((s) => s.openSidebar)
  const guardianViewActive = useUIStore((s) => s.guardianViewActive)
  const setGuardianView = useUIStore((s) => s.setGuardianView)

  const FAMILY_LINKED_ROLES = new Set(['family_admin', 'co_carer', 'teacher', 'therapist', 'medical', 'first_responder'])
  const hasOrgRole = roles.some(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)
  const hasFamilyRole = roles.some(r => FAMILY_LINKED_ROLES.has(r.role))
    || (managedProfiles ?? []).some(p => FAMILY_LINKED_ROLES.has(p.role))
  const isDualRole = hasOrgRole && hasFamilyRole
  const openSearch = useSearchModalStore((s) => s.open)
  const isAlertActive = isSOSActive || isPanicActive

  const unreadMsg = useMessagingUnread()
  const unreadBell = useBellUnread()

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showSignOut, setShowSignOut] = useState(false)
  const [isPillHovered, setIsPillHovered] = useState(false)
  const pillExpanded = isPillHovered || showUserMenu
  const menuRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showUserMenu) return
    function onClickOutside(e: MouseEvent) {
      if (!pillRef.current?.contains(e.target as Node)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [showUserMenu])

  const routeInfo = getRouteInfo(location.pathname)
  const title = routeInfo?.title ?? ''

  useEffect(() => {
    document.title = title ? `${title} — David's Beacon` : "David's Beacon"
  }, [title])

  function handleAlertClick() {
    const isOrg = primaryRole === USER_ROLES.ORG_ADMIN || primaryRole === USER_ROLES.STAFF
    const target = isSOSActive
      ? (isOrg ? ROUTES.MISSION_CONTROL : ROUTES.SOS)
      : ROUTES.MISSION_CONTROL
    if (location.pathname !== target) navigate(target)
  }

  function handleSignOut() {
    setShowSignOut(false)
    setShowUserMenu(false)
    // Shared recovery path (also works as https://davidsbeacon.com/logout).
    navigate(ROUTES.LOGOUT)
  }

  const effectiveHidden = scrollHidden && !isAlertActive

  const iconBtn = 'w-9 h-9 flex items-center justify-center rounded-full bg-beacon-50 text-slate-600 hover:bg-beacon-100 hover:text-slate-700 transition-colors flex-shrink-0'
  const badge = 'absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-danger-500 text-white text-[10px] font-bold flex items-center justify-center leading-none pointer-events-none'

  const showMsg = unreadMsg > 0 || pillExpanded
  const showBell = unreadBell > 0 || pillExpanded

  return (
    <>
      {/* Mobile header — full beacon bar */}
      <header
        onClick={isAlertActive ? handleAlertClick : undefined}
        className={[
          'app-header-mobile lg:hidden fixed top-0 left-0 right-0 z-40 h-16 border-b',
          'bg-beacon-600 border-beacon-700',
          isAlertActive ? 'cursor-pointer' : '',
          effectiveHidden ? '-translate-y-full' : 'translate-y-0',
          'transition-transform duration-300',
        ].join(' ')}
      >
        <div className="h-full flex items-center px-4 gap-2 relative">
          <button
            onClick={(e) => { e.stopPropagation(); openSidebar() }}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
            aria-label="Open menu"
          >
            <i className="fa-solid fa-circle-nodes text-xl text-white" />
          </button>

          <div className="absolute inset-x-0 flex justify-center items-center pointer-events-none">
            {title ? (
              <h1 className="font-semibold text-base text-white truncate max-w-[200px] sm:max-w-xs">
                {title}
              </h1>
            ) : (
              <div className="h-4 w-20 rounded-full bg-white/30" />
            )}
          </div>

          <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {isAlertActive && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                <span className="relative flex h-2 w-2">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isSOSActive ? 'bg-danger-500' : 'bg-warning-400'}`} />
                  <span className={`relative inline-flex h-2 w-2 rounded-full ${isSOSActive ? 'bg-danger-500' : 'bg-warning-400'}`} />
                </span>
                {isSOSActive ? 'SOS Active' : 'Panic Active'}
              </span>
            )}
            <div className="relative">
              <button
                onClick={() => navigate(ROUTES.MESSAGING)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-white hover:bg-white/20 active:bg-white/30 transition-colors"
                aria-label="Messages"
              >
                <i className="fa-solid fa-comments text-xl" />
              </button>
              {unreadMsg > 0 && (
                <span className={badge}>{unreadMsg > 9 ? '9+' : unreadMsg}</span>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => navigate(ROUTES.FEEDBACK)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-white hover:bg-white/20 active:bg-white/30 transition-colors"
                aria-label="Roadmap & Feedback"
              >
                <i className="fa-solid fa-bell text-xl" />
              </button>
              {unreadBell > 0 && (
                <span className={badge}>{unreadBell > 9 ? '9+' : unreadBell}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Desktop floating pill */}
      <div
        ref={pillRef}
        className="app-header-pill hidden 2xl:flex fixed top-4 right-4 z-[1001] items-center gap-2 px-3 py-2.5 bg-white rounded-full shadow-md border border-slate-200"
        onMouseEnter={() => setIsPillHovered(true)}
        onMouseLeave={() => setIsPillHovered(false)}
      >

        {/* Alert — auto-peek when active */}
        {isAlertActive && (
          <button
            onClick={handleAlertClick}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors cursor-pointer flex-shrink-0"
            style={{ background: isSOSActive ? 'rgb(254 226 226)' : 'rgb(254 243 199)', color: isSOSActive ? 'rgb(185 28 28)' : 'rgb(146 64 14)' }}
          >
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isSOSActive ? 'bg-danger-500' : 'bg-warning-400'}`} />
              <span className={`relative inline-flex h-2 w-2 rounded-full ${isSOSActive ? 'bg-danger-500' : 'bg-warning-400'}`} />
            </span>
            {isSOSActive ? 'SOS Active' : 'Panic Active'}
          </button>
        )}

        {/* Messages — auto-peek when unread, always visible when expanded */}
        {showMsg && (
          <div className="relative">
            <button onClick={() => navigate(ROUTES.MESSAGING)} className={iconBtn} aria-label="Messages">
              <i className="fa-solid fa-comments text-lg" />
            </button>
            {unreadMsg > 0 && (
              <span className={badge}>{unreadMsg > 9 ? '9+' : unreadMsg}</span>
            )}
          </div>
        )}

        {/* Bell — auto-peek when unread, always visible when expanded */}
        {showBell && (
          <div className="relative">
            <button onClick={() => navigate(ROUTES.FEEDBACK)} className={iconBtn} aria-label="Roadmap & Feedback">
              <i className="fa-solid fa-bell text-lg" />
            </button>
            {unreadBell > 0 && (
              <span className={badge}>{unreadBell > 9 ? '9+' : unreadBell}</span>
            )}
          </div>
        )}

        {/* Search — always visible */}
        <button onClick={openSearch} className={iconBtn} aria-label="Search (Ctrl K)">
          <i className="fa-solid fa-magnifying-glass text-base" />
        </button>

        {/* Avatar — always visible; click = toggle expand/collapse */}
        <div ref={menuRef} className="relative flex items-center">
          <button
            onClick={() => setShowUserMenu((v) => !v)}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 transition-all overflow-hidden"
            aria-label="Account menu"
          >
            <UserAvatar photoURL={user?.photoURL} displayName={user?.displayName} size="sm" />
          </button>

          {showUserMenu && (
            <div className="fixed mt-1 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-[1002]"
              style={{ top: pillRef.current ? pillRef.current.getBoundingClientRect().bottom : 0, right: 16 }}>
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-800 truncate">{user?.displayName ?? 'User'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
              {isDualRole && (
                <>
                  <button
                    onClick={() => {
                      const next = !guardianViewActive
                      setGuardianView(next)
                      // Stay on /home — /org used to auto-bridge to Lumen login.
                      navigate(ROUTES.HOME)
                      setShowUserMenu(false)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-beacon-700 hover:bg-beacon-50 transition-colors"
                  >
                    <i className="fa-solid fa-right-left w-4 text-center text-beacon-500" />
                    {guardianViewActive ? 'Switch to Organization' : 'Switch to Guardian View'}
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                </>
              )}
              <button
                onClick={() => { setShowUserMenu(false); navigate(ROUTES.SETTINGS) }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <i className="fa-solid fa-gear w-4 text-center text-slate-600" />
                Settings
              </button>
              <button
                onClick={() => { setShowUserMenu(false); navigate(ROUTES.ABOUT) }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <i className="fa-solid fa-circle-question w-4 text-center text-slate-600" />
                Help Center
              </button>
              <div className="flex items-center gap-3 px-4 py-2.5 opacity-40 cursor-not-allowed select-none">
                <i className="fa-solid fa-moon w-4 text-center text-slate-600 text-sm" />
                <span className="text-sm font-medium text-slate-700 flex-1">Dark Mode</span>
                <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">Soon</span>
              </div>
              {isSuperAdmin && (
                <>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    onClick={() => { setShowUserMenu(false); navigate(ROUTES.COMMAND_CENTER) }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50 transition-colors"
                  >
                    <i className="fa-solid fa-satellite-dish w-4 text-center" />
                    Command Center
                  </button>
                </>
              )}
              <div className="my-1 border-t border-slate-100" />
              <button
                onClick={() => { setShowUserMenu(false); setShowSignOut(true) }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
              >
                <i className="fa-solid fa-right-from-bracket w-4 text-center" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {showSignOut && (
        <ConfirmModal
          icon="fa-solid fa-right-from-bracket text-slate-500"
          iconBg="bg-slate-100"
          title="Sign out?"
          body="You'll need to sign in again to access your profiles and alerts."
          confirmLabel="Sign Out"
          confirmClass="bg-danger-500 hover:bg-danger-600"
          onCancel={() => setShowSignOut(false)}
          onConfirm={handleSignOut}
        />
      )}
    </>
  )
}
